import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Lightbulb, Archive, Check, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useRangeQuery } from "@/lib/useData";
import {
  PageTitle, Card, SectionTitle, Takeaway, LoadingBlocks, ErrorState, Forbidden,
} from "@/components/shared/primitives";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { pct, num, relativeLastActive } from "@/lib/format";

const LEVEL_STYLE = {
  Regular: "var(--brand-kawaii)",
  Occasional: "var(--brand-floral)",
  "Not yet": "var(--text-muted)",
};
const INVITE_STYLE = {
  pending: "var(--brand-floral)",
  accepted: "var(--brand-kawaii)",
  declined: "var(--border-control)",
};

function Invitations({ invitations, canManage, onDelete }) {
  if (!invitations || invitations.length === 0) return null;
  return (
    <Card className="p-5" data-testid="team-invitations">
      <SectionTitle className="mb-3">Invitations</SectionTitle>
      <div className="space-y-2">
        {invitations.map((i) => (
          <div key={i.id} className="flex items-center justify-between text-[14px]">
            <span className="text-nb-ink">{i.name}</span>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-2 text-[13px] text-nb-ink rounded-md px-2.5 py-1 capitalize" style={{ background: "var(--bg-sunken)" }}>
                <span className="w-2 h-2 rounded-full" style={{ background: INVITE_STYLE[i.status] }} />{i.status}
              </span>
              {canManage && i.status === "pending" && (
                <button onClick={() => onDelete(i)} data-testid={`delete-invite-${i.id}`} title="Cancel invitation" className="text-nb-ink2 hover:text-[var(--neg-text)] inline-flex">
                  <Trash2 size={15} strokeWidth={1.5} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function TeamDetail() {
  const { id } = useParams();
  const { member } = useAuth();
  const { data, isLoading, error, refetch } = useRangeQuery(`team-${id}`, `/teams/${id}`);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState("");
  const [confirm, setConfirm] = useState(null);
  if (error?.code === "forbidden") return <Forbidden who="Admins, IT Admins and Partners" />;
  if (isLoading) return <LoadingBlocks rows={4} />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  const d = data.data;
  const canManage = member?.role === "admin" || d.team.created_by === member?.id || d.team.lead_member_id === member?.id;

  const doUpdate = async (patch, msg) => {
    try { await api.patch(`/teams/${id}`, patch); toast.success(msg); refetch(); }
    catch (e) { toast.error(e.message); }
  };
  const saveName = () => { if (newName.trim()) doUpdate({ name: newName.trim() }, "Team renamed."); setRenaming(false); };
  const removeMember = (m) => setConfirm({
    title: `Remove ${m.name} from ${d.team.name}?`,
    description: "They stay in the firm directory and their past usage is kept, they are just no longer part of this team.",
    confirmLabel: "Remove from team",
    run: () => doUpdate({ remove_member_id: m.member_id }, "Member removed from team."),
  });
  const archiveTeam = () => setConfirm({
    title: `Archive ${d.team.name}?`,
    description: "The team is hidden from lists but its members and past usage are kept. You can restore it from here.",
    confirmLabel: "Archive team",
    run: () => doUpdate({ archived: true }, "Team archived."),
  });
  const deleteInvite = (i) => setConfirm({
    title: `Cancel the invitation to ${i.name}?`,
    description: "They will no longer be able to accept this invitation.",
    confirmLabel: "Cancel invitation",
    run: async () => { try { await api.del(`/invitations/${i.id}`); toast.success("Invitation cancelled."); refetch(); } catch (e) { toast.error(e.message); } },
  });

  return (
    <div>
      <Link to="/teams" className="inline-flex items-center gap-1.5 text-[13px] text-nb-ink2 hover:text-nb-ink mb-3 transition-colors">
        <ArrowLeft size={14} /> All teams
      </Link>
      {renaming ? (
        <div className="flex items-center gap-2 mb-4 max-w-md">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="rename-input" autoFocus />
          <Button onClick={saveName} className="rounded-[10px] bg-nb-navy text-nb-cream" data-testid="rename-save"><Check size={16} /></Button>
          <Button variant="outline" onClick={() => setRenaming(false)} className="rounded-[10px]"><X size={16} /></Button>
        </div>
      ) : (
        <PageTitle
          testid="team-detail-page"
          actions={canManage ? (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setNewName(d.team.name); setRenaming(true); }} className="rounded-[10px]" data-testid="rename-team">Rename</Button>
              <Button variant="outline" onClick={() => d.team.archived ? doUpdate({ archived: false }, "Team restored.") : archiveTeam()} className="rounded-[10px] gap-2" data-testid="archive-team"><Archive size={15} /> {d.team.archived ? "Restore" : "Archive"}</Button>
            </div>
          ) : null}
        >{d.team.name}{d.team.archived ? " (archived)" : ""}</PageTitle>
      )}
      {d.team.description && <Takeaway>{d.team.description}</Takeaway>}

      <div className="space-y-8">
        {d.group_too_small ? (
          <Card className="p-10 text-center" data-testid="team-too-small">
            <p className="text-[16px] text-nb-ink font-medium">Not enough people to show</p>
            <p className="text-[14px] text-nb-ink2 mt-2">This team has fewer than five members, so individual usage cannot be inferred from a total.</p>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Members", value: num(d.cards.members) },
                { label: "Using any AI", value: num(d.cards.members_using) },
                { label: "Adoption", value: pct(d.cards.adoption) },
                { label: "Tools used", value: num(d.cards.tools_used) },
              ].map((c) => (
                <Card key={c.label} className="p-5">
                  <div className="nb-label mb-1">{c.label}</div>
                  <div className="font-display text-[26px] font-bold text-nb-ink tnum">{c.value}</div>
                </Card>
              ))}
            </div>

            {d.suggestion && (
              <Card className="p-4 flex items-start gap-3" data-testid="team-suggestion" style={{ backgroundColor: "rgba(146,186,213,0.22)" }}>
                <Lightbulb size={18} strokeWidth={1.5} className="text-nb-ink shrink-0 mt-0.5" />
                <p className="text-[14px] text-nb-ink">{d.suggestion}</p>
              </Card>
            )}

            <Card className="p-5">
              <SectionTitle className="mb-1">Tools this team uses</SectionTitle>
              <Takeaway>Each tool with its most-used features.</Takeaway>
              {d.tools.length === 0 ? (
                <p className="text-[14px] text-nb-ink2">No tool usage yet.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {d.tools.map((t) => (
                    <div key={t.app_id} className="rounded-xl p-4" style={{ border: "1px solid var(--border-subtle)" }}>
                      <div className="flex items-center justify-between mb-2">
                        <Link to={`/apps/${t.app_id}`} className="text-[14px] font-medium text-nb-ink hover:text-[var(--text-link)]">{t.app}</Link>
                        <span className="text-[13px] text-nb-ink2 tnum">{t.active_users} active</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {t.feature_mix.map((f) => (
                          <span key={f.feature} className="text-[12.5px] text-nb-ink2 rounded-md px-2.5 py-0.5" style={{ background: "var(--bg-sunken)" }}>{f.feature} · {f.uses}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card className="p-5" data-testid="team-members">
              <SectionTitle className="mb-3">Members</SectionTitle>
              <div className="overflow-x-auto">
                <table className="w-full text-[14px]">
                  <thead>
                    <tr className="nb-label text-left" style={{ background: "var(--bg-sunken)" }}>
                      <th className="py-2.5 px-3 first:rounded-l-md">Member</th>
                      <th className="py-2.5 px-3 text-right">Tools used</th>
                      <th className="py-2.5 px-3">Last active</th>
                      <th className="py-2.5 px-3 last:rounded-r-md">Usage level</th>
                      {canManage && <th className="py-2.5 px-3 last:rounded-r-md"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {d.members.map((m) => (
                      <tr key={m.member_id} className="border-b last:border-0 transition-colors hover:bg-[var(--bg-selected)]" style={{ borderColor: "var(--border-subtle)" }}>
                        <td className="py-3 px-3"><Link to={`/members/${m.member_id}`} className="text-nb-ink hover:text-[var(--text-link)]">{m.name}</Link></td>
                        <td className="py-3 px-3 tnum text-right">{m.tools_used}</td>
                        <td className="py-3 px-3 text-nb-ink2">{relativeLastActive(m.last_active)}</td>
                        <td className="py-3 px-3">
                          <span className="inline-flex items-center gap-1.5 text-[13px]">
                            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: LEVEL_STYLE[m.usage_level] }} />
                            {m.usage_level}
                          </span>
                        </td>
                        {canManage && (
                          <td className="py-3 px-3 text-right">
                            <button onClick={() => removeMember(m)} className="text-[13px] text-nb-ink2 hover:text-[var(--neg-text)]" data-testid={`remove-member-${m.member_id}`}>Remove</button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}

        <Invitations invitations={d.invitations} canManage={canManage} onDelete={deleteInvite} />
      </div>

      <ConfirmDialog
        open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm?.title} description={confirm?.description}
        confirmLabel={confirm?.confirmLabel || "Confirm"}
        onConfirm={() => { confirm?.run(); setConfirm(null); }}
      />
    </div>
  );
}
